import { API_ENDPOINTS, API_ERROR_MESSAGES } from "../constants/api";
import { buildNebulaApiUrl } from "./apiConfig";
import { ensureValidAccessToken, refreshAuthSession } from "./authSession";

async function applicationApiFetch(path, options) {
  const accessToken = await ensureValidAccessToken();

  if (!accessToken) {
    throw new Error(API_ERROR_MESSAGES.authenticatedAccessTokenMissing);
  }

  const buildOptions = (token) => ({
    ...options,
    headers: {
      ...options?.headers,
      "x-access-token": token,
    },
  });
  const url = buildNebulaApiUrl(path);
  let response = await fetch(url, buildOptions(accessToken));

  if (response.status === 401) {
    const refreshedSession = await refreshAuthSession({ force: true });
    response = await fetch(url, buildOptions(refreshedSession.accessToken));
  }

  return response;
}

async function readJsonResponse(response, errorMessage) {
  try {
    return await response.json();
  } catch {
    throw new Error(errorMessage);
  }
}

function getApplicationId(application) {
  return (
    application?.id ||
    application?.applicationId ||
    application?.application_id ||
    ""
  );
}

function candidateApplicationsFrom(value) {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== "object") {
    return [];
  }

  if (getApplicationId(value)) {
    return [value];
  }

  const nestedCandidates = [
    value.data,
    value.content,
    value.items,
    value.rows,
    value.results,
    value.applications,
    value.applicationSummaries,
    value.application_summaries,
  ];

  for (const candidate of nestedCandidates) {
    const applications = candidateApplicationsFrom(candidate);

    if (applications.length > 0) {
      return applications;
    }
  }

  return [];
}

function unwrapApplicationDetails(result) {
  const candidates = [
    result?.data,
    result?.application,
    result?.applicationDetails,
    result?.application_details,
    result,
  ];

  return (
    candidates.find(
      (candidate) =>
        candidate &&
        typeof candidate === "object" &&
        !Array.isArray(candidate) &&
        (getApplicationId(candidate) ||
          candidate.personalDetails ||
          candidate.personal_details),
    ) || null
  );
}

export async function searchApplicationSummaryByLeadId(leadId) {
  const response = await applicationApiFetch(
    API_ENDPOINTS.application.summarySearch,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        page: 1,
        pageSize: 100,
        leadId,
        sortBy: "UPDATED_AT",
        sortOrder: "DESC",
      }),
    },
  );

  const result = await readJsonResponse(
    response,
    API_ERROR_MESSAGES.applicationSearchReadFailed,
  );

  if (!response.ok || result?.success === false) {
    throw new Error(
      result?.message || API_ERROR_MESSAGES.applicationSearchFailed,
    );
  }

  const applicationSummary = candidateApplicationsFrom(result).find(
    getApplicationId,
  );

  if (!applicationSummary) {
    throw new Error(result?.message || API_ERROR_MESSAGES.applicationNotFound);
  }

  return applicationSummary;
}

export async function fetchApplicationById(applicationId) {
  const response = await applicationApiFetch(
    API_ENDPOINTS.application.details(applicationId),
    {
      method: "GET",
    },
  );

  const result = await readJsonResponse(
    response,
    API_ERROR_MESSAGES.applicationDetailsReadFailed,
  );

  if (!response.ok || result?.success === false) {
    throw new Error(
      result?.message || API_ERROR_MESSAGES.applicationDetailsFetchFailed,
    );
  }

  return unwrapApplicationDetails(result) || result;
}

export async function fetchApplicationByLeadId(leadId) {
  const summary = await searchApplicationSummaryByLeadId(leadId);
  const applicationId = getApplicationId(summary);
  const application = await fetchApplicationById(applicationId);

  return {
    application,
    applicationId,
    summary,
  };
}
